import { normalizeRedeemCode } from "@/lib/utils";
// Determine API base dynamically to avoid hard-coded http:// links that cause mixed content under HTTPS.
// Priority:
// 1. Explicit build-time env var VITE_API_BASE_URL (should include /api)
// 2. Same-origin '/api' (works when frontend and backend are served via one domain behind reverse proxy)
// 3. Fallback local dev default
let API_BASE = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE) {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    API_BASE = `${window.location.origin}/api`;
  } else {
    // In non-window environments (SSR/build tools), prefer same-origin '/api' placeholder.
    // Most usages run in the browser; for server-side utilities, allow the caller to inject VITE_API_BASE_URL.
    API_BASE = "/api";
  }
}
// If someone accidentally built with a localhost API base but we're not on localhost,
// prefer same-origin '/api' to avoid connection refused on real servers.
if (
  API_BASE &&
  /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(API_BASE) &&
  typeof window !== "undefined" &&
  window.location &&
  window.location.hostname &&
  !/(^localhost$)|(^127\.)/i.test(window.location.hostname)
) {
  API_BASE = `${window.location.origin}/api`;
}
// Normalize: remove any trailing slash (except the one in protocol) to prevent double slashes in requests
API_BASE = API_BASE.replace(/\/$/, "");

// In-Memory Access Token Store (keine Persistenz, Refresh Cookie bleibt HttpOnly im Browser)
let currentAccessToken = null;
let isRefreshing = false;
const refreshWaiters = [];

export function setAccessToken(t) { currentAccessToken = t; }
export function getAccessToken() { return currentAccessToken; }
export function clearAccessToken() { currentAccessToken = null; }

async function performRefresh() {
  const hadAccessTokenAtStart = !!currentAccessToken;
  if (isRefreshing) {
    return new Promise((resolve, reject) => refreshWaiters.push({ resolve, reject }));
  }
  isRefreshing = true;
  try {
    // Do not refresh impersonation tokens (staff/agency remote access)
    try {
      if (typeof window !== "undefined" && currentAccessToken) {
        const parts = currentAccessToken.split(".");
        if (parts.length === 3) {
          const payloadJson = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
          const payload = JSON.parse(payloadJson || "{}");
          if (payload && (payload.type === "staff_access" || payload.type === "agency_access")) {
            clearAccessToken();
            try {
              window.dispatchEvent(
                new CustomEvent("auth:logout", { detail: { reason: "impersonation_expired" } })
              );
            } catch (_) {}
            throw new Error("Impersonation token cannot be refreshed");
          }
        }
      }
    } catch (_) {
      // If decoding fails, fall through to normal refresh logic
    }

    const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
    const data = await res.json().catch(()=>null);
    if (!res.ok) throw new Error(data?.error || 'Refresh failed');
    if (data?.token) setAccessToken(data.token);
    refreshWaiters.forEach(w => w.resolve(data.token));
    refreshWaiters.length = 0;
    return data.token;
  } catch (e) {
    refreshWaiters.forEach(w => w.reject(e));
    refreshWaiters.length = 0;
    clearAccessToken();
    // Broadcast forced logout so AuthContext clears user state.
    // Only do this if we actually had an access token to refresh; otherwise we can accidentally
    // log out staff/customer sessions that don't use JWT refresh.
    if (hadAccessTokenAtStart) {
      try { window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'refresh_failed' } })); } catch(_) {}
    }
    throw e;
  } finally {
    isRefreshing = false;
  }
}

async function request(path, options = {}) {
  // Dev debug: log outgoing create campaign payload to diagnose missing 'name' on backend
  if (
    import.meta.env.DEV &&
    path === "/campaigns" &&
    (options.method || "GET").toUpperCase() === "POST"
  ) {
    try {
      const raw = options.body;
      console.debug("[frontend][POST /campaigns] raw body string:", raw);
      if (typeof raw === "string") {
        try {
          console.debug(
            "[frontend][POST /campaigns] parsed object:",
            JSON.parse(raw)
          );
        } catch (e) {
          console.debug("[frontend][POST /campaigns] JSON.parse failed", e);
        }
      }
    } catch (_) {
      /* ignore */
    }
  }
  // IMPORTANT: spread options first, then merge headers so that our default
  // Content-Type is not overwritten by the later object spread. Previously we had
  // { headers: {...}, ...options } which let options.headers replace the whole
  // headers object (dropping Content-Type). That caused the browser to send
  // text/plain and the backend saw an empty body.
  const authHeader = currentAccessToken ? { Authorization: `Bearer ${currentAccessToken}` } : {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}), ...authHeader },
    credentials: options.credentials || 'include',
  });
  let data;
  try {
    data = await res.json();
  } catch (_) {
    data = null;
  }
  if (!res.ok) {
    // Silent refresh logic on auth errors
    const code = data?.code;
    
    // If session was revoked, clear auth and trigger logout.
    // Some calls (e.g. auth/customer session probes during app bootstrap) intentionally pass
    // `skipRefresh: true` and expect 401/SESSION_REVOKED without forcing a global logout.
    if (code === 'SESSION_REVOKED') {
      clearAccessToken();
      if (typeof window !== 'undefined' && !options.skipRefresh) {
        window.dispatchEvent(new Event('auth:logout'));
      }
      const err = new Error('Session revoked');
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    
    if (res.status === 401 && ['TOKEN_EXPIRED','INVALID_TOKEN','MISSING_AUTH'].includes(code || '') && !options._retry && !options.skipRefresh && currentAccessToken) {
      try {
        await performRefresh();
        // Retry original request with flag
        return await request(path, { ...options, _retry: true });
      } catch (_) {
        // propagate original error after failed refresh
      }
    }
    const msg = data?.error || data?.message || `API error ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export const api = {
  registerCompany: (data) =>
    request("/auth/register", { method: "POST", body: JSON.stringify(data) }),
  login: async (data) => {
    const resp = await request("/auth/login", { method: "POST", body: JSON.stringify(data) });
    if (resp?.token) setAccessToken(resp.token);
    return resp;
  },
  verifyLicense: (licenseKey) =>
    request("/auth/license/verify", {
      method: "POST",
      body: JSON.stringify({ licenseKey }),
    }),
  activateLicense: async (data) => {
    const resp = await request("/auth/activate", { method: "POST", body: JSON.stringify(data) });
    if (resp?.token) setAccessToken(resp.token);
    return resp;
  },
  refresh: async () => {
    const resp = await request('/auth/refresh', { method: 'POST', skipRefresh: true });
    if (resp?.token) setAccessToken(resp.token);
    return resp;
  },
  logout: async () => {
    try { await request('/auth/logout', { method: 'POST' }); } catch (_) {}
    clearAccessToken();
  },
  ownerSetPassword: (data) =>
    request("/auth/owner-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  // System owner agency management
  listAgencies: async (token) => {
    const data = await request("/system/agencies", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  },
  createAgency: (token, payload) =>
    request("/system/agencies", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateAgency: (token, id, payload) =>
    request(`/system/agencies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: { Authorization: `Bearer ${token}` },
    }),
  inviteAgencyOwner: (token, id, email) =>
    request(`/system/agencies/${id}/invite-owner`, {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Agency billing (SYSTEM_OWNER)
  systemAgencyStatement: (token, agencyId, months = 6) =>
    request(
      `/system/agencies/${encodeURIComponent(agencyId)}/statement?months=${encodeURIComponent(months)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    ),
  systemAgencyInvoiceUploads: (token, agencyId) =>
    request(`/system/agencies/${encodeURIComponent(agencyId)}/invoice-uploads`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  systemAgencyInvoiceUpload: async (token, agencyId, month, file) => {
    const fd = new FormData();
    fd.append("month", month);
    fd.append("file", file);
    const res = await fetch(`${API_BASE}/system/agencies/${encodeURIComponent(agencyId)}/invoice-uploads`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
      credentials: "include",
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || data?.message || `API error ${res.status}`);
    return data;
  },
  systemAgencyInvoiceDownload: async (token, agencyId, uploadId) => {
    const res = await fetch(
      `${API_BASE}/system/agencies/${encodeURIComponent(agencyId)}/invoice-uploads/${encodeURIComponent(uploadId)}/download`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.error || data?.message || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const contentDisposition = res.headers.get("content-disposition") || "";
    let filename = null;
    const m = /filename="?([^";]+)"?/i.exec(contentDisposition);
    if (m) filename = m[1];
    return { blob, filename };
  },
  // Agency side: linked organizations & remote access
  agencyOrganizations: (token) =>
    request(`/agency/organizations`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyCreateOrganization: (token, payload) =>
    request(`/agency/organizations`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencySwitchOrganization: (token, companyId) =>
    request(`/agency/switch-organization`, {
      method: "POST",
      body: JSON.stringify({ companyId }),
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Agency side: reusable Test-QR codes for remote-access organizations
  agencyTestQrCampaigns: (token, companyId) =>
    request(`/agency/test-qr/campaigns?companyId=${encodeURIComponent(companyId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyTestQrActions: (token, companyId, campaignId) =>
    request(
      `/agency/test-qr/actions?companyId=${encodeURIComponent(companyId)}&campaignId=${encodeURIComponent(campaignId)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    ),
  agencyCreateTestQr: (token, companyId, actionId) =>
    request(`/agency/test-qr`, {
      method: "POST",
      body: JSON.stringify({ companyId, actionId }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyUsage: (token, days) =>
    request(`/agency/usage?days=${encodeURIComponent(days ?? 30)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
    agencyTimeseries: (token, days) =>
      request(`/agency/timeseries?days=${encodeURIComponent(days ?? 30)}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  agencyInvoices: (token, months) =>
    request(`/agency/invoices?months=${encodeURIComponent(months ?? 6)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyInvoiceUploads: (token) =>
    request(`/agency/invoice-uploads`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyInvoiceDownload: async (token, uploadId) => {
    const res = await fetch(`${API_BASE}/agency/invoice-uploads/${encodeURIComponent(uploadId)}/download`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.error || data?.message || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const contentDisposition = res.headers.get("content-disposition") || "";
    let filename = null;
    const m = /filename="?([^";]+)"?/i.exec(contentDisposition);
    if (m) filename = m[1];
    return { blob, filename };
  },
  // System owner organization management
  listOrganizations: async (token) => {
    const data = await request("/system/organizations", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Support both old (array) and new (wrapped) response shapes
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  },
  createOrganization: (token, name, email) =>
    request("/system/organizations", {
      method: "POST",
      body: JSON.stringify(email ? { name, email } : { name }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  organizationDetail: (token, id) =>
    request(`/system/organizations/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  organizationLicense: (token, id) =>
    request(`/system/organizations/${id}/license`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateOrganization: (token, id, data) =>
    request(`/system/organizations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  setOrganizationStatus: (token, id, status) =>
    request(`/system/organizations/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  requestPasswordReset: (token, id) =>
    request(`/system/organizations/${id}/request-password-reset`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),
  resetPassword: (token, newPassword) =>
    request(`/auth/reset-password`, {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    }),
  rotateLicense: (token, id) =>
    request(`/system/organizations/${id}/rotate-license`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),
  inviteOwner: (token, id, email) =>
    request(`/system/organizations/${id}/invite-owner`, {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  listInvites: (token, id) =>
    request(`/system/organizations/${id}/invites`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  resendInvite: (token, id, inviteId) =>
    request(`/system/organizations/${id}/invites/${inviteId}/resend`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),
  deleteInvite: (token, id, inviteId) =>
    request(`/system/organizations/${id}/invites/${inviteId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Employee management (ORG_OWNER)
  listEmployees: (token) =>
    request("/employees", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  employeeUsage: (token) =>
    request("/employees/meta/usage", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateEmployeeLimit: (token, employeeLimit) =>
    request("/employees/meta/limit", {
      method: "PATCH",
      body: JSON.stringify({ employeeLimit }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  createEmployee: (token, data) =>
    request("/employees", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  deleteEmployee: (token, id) =>
    request(`/employees/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  createCampaign: (token, data) =>
    request("/campaigns", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  listCampaigns: (token) =>
    request("/campaigns", { headers: { Authorization: `Bearer ${token}` } }),
  updateCampaign: (token, id, data) =>
    request(`/campaigns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  deleteCampaign: (token, id) =>
    request(`/campaigns/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  createAction: (token, data) =>
    request("/actions", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  actionStats: (token, id) =>
    request(`/actions/stats/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  listActions: (token, campaignId) =>
    request(`/actions${campaignId ? `?campaignId=${campaignId}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  getAction: (token, id) =>
    request(`/actions/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  searchActions: (token, { q = "", campaignId = null, limit = 20 } = {}) => {
    const qp = new URLSearchParams();
    if (q) qp.set('q', q);
    if (campaignId) qp.set('campaignId', campaignId);
    if (limit) qp.set('limit', String(limit));
    return request(`/actions?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  updateAction: (token, id, data) =>
    request(`/actions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  deleteAction: (token, id) =>
    request(`/actions/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  actionScans: (token, id, { date, limit = 10, offset = 0 } = {}) => {
    const qp = new URLSearchParams();
    if (date) qp.set('date', date);
    qp.set('limit', String(limit));
    qp.set('offset', String(offset));
    return request(`/actions/${id}/scans?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  actionRedeems: (token, id, { date, limit = 10, offset = 0 } = {}) => {
    const qp = new URLSearchParams();
    if (date) qp.set('date', date);
    qp.set('limit', String(limit));
    qp.set('offset', String(offset));
    return request(`/actions/${id}/redeems?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  updateActionLandingSettings: (token, id, data) =>
    request(`/actions/${id}/landing-settings`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadActionBanner: (token, id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/actions/${id}/banner`, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  listLeads: (token) =>
    request("/leads", { headers: { Authorization: `Bearer ${token}` } }),
  suppressLead: (token, data) =>
    request(`/leads/suppress`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  unsuppressLead: (token, id) =>
    request(`/leads/suppress/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  listCodes: (token) =>
    request("/codes", { headers: { Authorization: `Bearer ${token}` } }),
  listUsers: (token) =>
    request("/users", { headers: { Authorization: `Bearer ${token}` } }),
  createUser: (token, data) =>
    request("/users", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  deleteUser: (token, id) =>
    request(`/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Agency employees (AGENCY role)
  agencyListUsers: (token) =>
    request("/agency/users", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyCreateUser: (token, data) =>
    request("/agency/users", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyDeleteUser: (token, id) =>
    request(`/agency/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyInviteUser: (token, data) =>
    request("/agency/invitations", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  agencyAccessLogs: (token, { days = 30, limit = 200, page = 1 } = {}) =>
    request(
      `/agency/access-logs?days=${encodeURIComponent(days)}&limit=${encodeURIComponent(limit)}&page=${encodeURIComponent(page)}`,
      {
      headers: { Authorization: `Bearer ${token}` },
      }
    ),
  agencyToggleUserActive: (token, id) =>
    request(`/agency/users/${encodeURIComponent(id)}/toggle`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    }),
  publicScan: (data) =>
    request("/public/scan/start", { method: "POST", body: JSON.stringify(data) }),
  publicVerify: (token) =>
    request(`/public/scan/verify/${token}`, { method: "POST" }),
  publicClaim: (code) => request(`/public/scan/claim/${code}`),
  redeemEmployee: (token, code) =>
    request(`/codes/redeem/${encodeURIComponent(normalizeRedeemCode(code))}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),
  codeStatus: (token, code) =>
    request(`/codes/status/${encodeURIComponent(normalizeRedeemCode(code))}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  qrActionMeta: (slug) => request(`/qr/action/${slug}`),
  qrCode: (slug) => request(`/qr/code/${slug}`),
  analyticsOverview: (token) =>
    request("/analytics/overview", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  analyticsGlobalOverview: (token, { agencyId } = {}) => {
    const qp = new URLSearchParams();
    if (agencyId) qp.set("agencyId", agencyId);
    const suffix = qp.toString() ? `?${qp.toString()}` : "";
    return request(`/analytics/global/overview${suffix}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  analyticsTimeseries: (token, days = 30) =>
    request(`/analytics/timeseries?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  analyticsGlobalTimeseries: (token, days = 30, { agencyId } = {}) => {
    const qp = new URLSearchParams();
    qp.set("days", String(days));
    if (agencyId) qp.set("agencyId", agencyId);
    return request(`/analytics/global/timeseries?${qp.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  analyticsRange: (token, { start, end, actionId = null, actionIds = [], granularity = 'auto', includeMeta = false }) => {
    const qp = new URLSearchParams();
    qp.set('start', start);
    qp.set('end', end);
    if (actionId) qp.set('actionId', actionId);
    if (Array.isArray(actionIds) && actionIds.length) qp.set('actionIds', actionIds.join(','));
    if (granularity) qp.set('granularity', granularity);
    if (includeMeta) qp.set('includeMeta', '1');
    return request(`/analytics/range?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  analyticsDay: (token, date) =>
    request(`/analytics/day?date=${date}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  analyticsActionsPerformance: (token, { start, end } = {}) => {
    const qp = new URLSearchParams();
    if (start) qp.set('start', start);
    if (end) qp.set('end', end);
    const suffix = qp.toString() ? `?${qp.toString()}` : "";
    return request(`/analytics/actions-performance${suffix}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  analyticsLeadsRange: (token, { start, end, actionIds = [] }) => {
    const qp = new URLSearchParams();
    qp.set('start', start);
    qp.set('end', end);
    if (Array.isArray(actionIds) && actionIds.length) qp.set('actionIds', actionIds.join(','));
    return request(`/analytics/leads-range?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  // SMTP identities (SYSTEM_OWNER)
  smtpList: (token) =>
    request(`/system/smtp-identities`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  smtpCreate: (token, data) =>
    request(`/system/smtp-identities`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  smtpUpdate: (token, id, data) =>
    request(`/system/smtp-identities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  smtpTest: (token, id, data) =>
    request(`/system/smtp-identities/${id}/test`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Email templates (SYSTEM_OWNER)
  emailTemplatesList: (token) =>
    request(`/system/email-templates`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  emailTemplateGet: (token, key, companyId = null) =>
    request(`/system/email-templates/${encodeURIComponent(key)}${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  emailTemplateUpsert: (token, key, data) =>
    request(`/system/email-templates/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  emailTemplateTest: (token, key, data) =>
    request(`/system/email-templates/${encodeURIComponent(key)}/test`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // System settings (SYSTEM_OWNER)
  systemSettingGet: (token, key) =>
    request(`/system/settings/${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  systemSettingSet: (token, key, value) =>
    request(`/system/settings/${encodeURIComponent(key)}` ,{
      method: 'PUT',
      body: JSON.stringify({ value }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Meta App OAuth config (SYSTEM_OWNER)
  metaAppConfigGet: (token) =>
    request(`/system/meta-app-config`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  metaAppConfigUpdate: (token, data) =>
    request(`/system/meta-app-config`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Public settings (no auth)
  publicSetting: async (key) => {
    const res = await request(`/public/settings/${encodeURIComponent(key)}`);
    return res?.value ?? null;
  },
  // Org self endpoints (ORG_OWNER)
  myCompany: (token) =>
    request(`/system/company`, { headers: { Authorization: `Bearer ${token}` } }),
  updateMyCompany: (token, data) =>
    request(`/system/company`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  companyAgencyOptions: (token) =>
    request(`/system/company/agency-options`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateCompanyAgencyLink: (token, agencyId) =>
    request(`/system/company/agency-link`, {
      method: "PATCH",
      body: JSON.stringify({ agencyId }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Company Meta CAPI settings
  getCompanyCapi: (token) =>
    request(`/system/company/capi`, { headers: { Authorization: `Bearer ${token}` } }),
  updateCompanyCapi: (token, data) =>
    request(`/system/company/capi`, { method: 'PATCH', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  // Meta OAuth connection (ORG_OWNER)
  getMetaOAuthUrl: (token) =>
    request(`/system/company/meta-oauth-url`, { headers: { Authorization: `Bearer ${token}` } }),
  // Meta campaign mappings (ORG_OWNER)
  getMetaMappings: (token) =>
    request(`/system/company/meta-mappings`, { headers: { Authorization: `Bearer ${token}` } }),
  saveMetaMappings: (token, mappings) =>
    request(`/system/company/meta-mappings`, { method: 'POST', body: JSON.stringify({ mappings }), headers: { Authorization: `Bearer ${token}` } }),
  // Meta campaigns (ORG_OWNER)
  getMetaCampaigns: (token) =>
    request(`/system/company/meta-campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
  // Trigger Meta insights sync for this company (ORG_OWNER)
  metaSync: (token, { since, until } = {}) =>
    request(`/system/company/meta-sync`, {
      method: 'POST',
      body: JSON.stringify({ since, until }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  // Trigger Meta insights sync for a single action's mapped campaign (ORG_OWNER)
  metaSyncForAction: (token, { actionId, since, until }) =>
    request(`/system/company/meta-sync-action`, {
      method: 'POST',
      body: JSON.stringify({ actionId, since, until }),
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadCompanyLogo: async (token, file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/system/company/logo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = data?.error || `Upload error ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data;
  },
  // Page Builder
  listPages: (token) => request('/pages', { headers: { Authorization: `Bearer ${token}` } }),
  createPage: (token, data) => request('/pages', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  updatePage: (token, id, data) => request(`/pages/${id}`, { method: 'PUT', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  publishPage: (token, id) => request(`/pages/${id}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  listPageVersions: (token, id) => request(`/pages/${id}/versions`, { headers: { Authorization: `Bearer ${token}` } }),
  rollbackPageVersion: (token, id, version) => request(`/pages/${id}/rollback/${version}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  previewPage: (token, id) => request(`/pages/${id}/preview`, { headers: { Authorization: `Bearer ${token}` } }),
  listThemes: (token) => request('/themes', { headers: { Authorization: `Bearer ${token}` } }),
  createTheme: (token, data) => request('/themes', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  ensureActionLandingPage: (token, actionId) => request(`/actions/${actionId}/landing-page`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  ensureActionDisplayPage: (token, actionId) => request(`/actions/${actionId}/landing-page?kind=display`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  uploadPageAsset: async (token, pageId, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE}/pages/${encodeURIComponent(pageId)}/assets`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const data = await res.json().catch(()=>null);
    if(!res.ok) throw new Error(data?.error || `Upload failed ${res.status}`);
    return data;
  },
  uploadNewsletterAsset: async (token, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE}/newsletters/assets`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const data = await res.json().catch(()=>null);
    if(!res.ok) throw new Error(data?.error || `Upload failed ${res.status}`);
    return data;
  },
  listAssetLibrary: (token, { kind = 'all', scope = 'all', limit = 400 } = {}) =>
    request(`/assets/library?kind=${encodeURIComponent(kind)}&scope=${encodeURIComponent(scope)}&limit=${encodeURIComponent(limit)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  listAssetFolders: (token, { scope = 'all' } = {}) =>
    request(`/assets/folders?scope=${encodeURIComponent(scope)}`, { headers: { Authorization: `Bearer ${token}` } }),
  listAssetLibraryPage: (token, { kind = 'all', scope = 'all', prefix, q, page = 1, pageSize = 9, cursor } = {}) => {
    const params = new URLSearchParams();
    params.set('kind', kind);
    params.set('scope', scope);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (prefix) params.set('prefix', prefix);
    if (q) params.set('q', q);
    if (cursor) params.set('cursor', cursor);
    return request(`/assets/library?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  deleteAssetItem: async (token, key) => {
    const res = await fetch(`${API_BASE}/assets/item?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(()=>null);
    if(!res.ok) throw new Error(data?.error || `Delete failed ${res.status}`);
    return data;
  },
  // Wallet (Google)
  walletGoogleCreate: async (codeId) => {
    const res = await fetch(`${API_BASE}/wallet/google/create`, { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ codeId }) });
    const data = await res.json().catch(()=>null);
    if(!res.ok) throw new Error(data?.error || `Wallet error ${res.status}`);
    return data;
  },
  // Sendmirein / Newsletters - Send Operations
  newslettersSend: (token, data) => request('/newsletters/send', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  newslettersSendTest: (token, data) => request('/newsletters/send-test', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  newslettersSendsList: (token) => request('/newsletters/sends', { headers: { Authorization: `Bearer ${token}` } }),
  newslettersGetSend: (token, id) => request(`/newsletters/sends/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  newslettersGetSendLogs: (token, id, page = 1, limit = 5) => request(`/newsletters/sends/${id}/logs?page=${page}&limit=${limit}`, { headers: { Authorization: `Bearer ${token}` } }),
  newslettersCancelSend: (token, id) => request(`/newsletters/sends/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
  // Sendmirein / Newsletters - Templates
  newslettersTemplatesList: (token) => request('/newsletters/templates', { headers: { Authorization: `Bearer ${token}` } }),
  newslettersTemplatesGet: (token, id) => request(`/newsletters/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  newslettersTemplatesCreate: (token, data) => request('/newsletters/templates', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  newslettersTemplatesUpdate: (token, id, data) => request(`/newsletters/templates/${id}`, { method: 'PUT', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  newslettersTemplatesDelete: (token, id) => request(`/newsletters/templates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
  newslettersTemplatesExport: async (token, id) => {
    const res = await fetch(`${API_BASE}/newsletters/templates/${id}/export`, { headers: { Authorization: `Bearer ${token}` } });
    const text = await res.text();
    // Try to parse JSON; if not ok, throw based on parsed or text
    let data = null;
    try { data = JSON.parse(text); } catch {}
    if(!res.ok){
      throw new Error((data && data.error) || `Export failed ${res.status}`);
    }
    // When ok, return both raw text and parsed
    return { raw: text, json: data };
  },
  newslettersTemplatesImport: (token, payload) => request('/newsletters/templates/import', { method: 'POST', body: JSON.stringify(payload), headers: { Authorization: `Bearer ${token}` } }),
  newslettersSubscribersList: (token, subscribed) => request(`/newsletters/subscribers/list${subscribed !== undefined ? `?subscribed=${subscribed}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
  newslettersSubscriberCount: (token) => request('/newsletters/subscribers/count', { headers: { Authorization: `Bearer ${token}` } }),
  newslettersSubscribersByAction: (token) => request('/newsletters/subscribers/by-action', { headers: { Authorization: `Bearer ${token}` } }),
  newslettersSubscribersPreviewCount: (token, recipientFilter) => request('/newsletters/subscribers/preview-count', { method: 'POST', body: JSON.stringify({ recipientFilter }), headers: { Authorization: `Bearer ${token}` } }),
  newslettersSmtpGet: (token) => request('/newsletters/smtp', { headers: { Authorization: `Bearer ${token}` } }),
  newslettersSmtpSave: (token, data) => request('/newsletters/smtp', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  newslettersSmtpTest: (token) => request('/newsletters/smtp/test', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  // Sessions (all authenticated roles)
  listSessions: (token) => request('/auth/sessions', { headers: { Authorization: `Bearer ${token}` } }),
  revokeSession: (token, id) => request(`/auth/sessions/${id}` , { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
  revokeOtherSessions: (token) => request('/auth/sessions/revoke-others', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  
  // Staff Management (System Admin only)
  staffList: (token) => request('/staff', { headers: { Authorization: `Bearer ${token}` } }),
  staffCreate: (token, data) => request('/staff', { method: 'POST', body: JSON.stringify(data), headers: { Authorization: `Bearer ${token}` } }),
  staffDelete: (token, staffId) => request(`/staff/${staffId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
  staffToggle: (token, staffId) => request(`/staff/${staffId}/toggle`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }),
  staffResendInvitation: (token, staffId) => request(`/staff/${staffId}/resend-invitation`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  
  // Staff Authentication & Management
  staffVerifyInvitation: (token) => request(`/staff/invitation/${token}`),
  staffSetPassword: (data) => request('/staff/set-password', { method: 'POST', body: JSON.stringify(data) }),
  agencySetPassword: (data) => request('/auth/agency-set-password', { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } }),
  staffLogin: (data) => request('/staff/login', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  staffLogout: () => request('/staff/logout', { method: 'POST', credentials: 'include' }),
  staffGetMe: () => request('/staff/me', { credentials: 'include', skipRefresh: true }),
  staffGetOrganizations: () => request('/staff/organizations', { credentials: 'include' }),
  staffSwitchOrganization: (companyId) => request('/staff/switch-organization', { method: 'POST', body: JSON.stringify({ companyId }), credentials: 'include' }),
  
  // Staff 2FA
  staff2faStatus: () => request('/staff/2fa/status', { credentials: 'include' }),
  staff2faSetup: () => request('/staff/2fa/setup', { method: 'POST', credentials: 'include' }),
  staff2faEnable: (token) => request('/staff/2fa/enable', { method: 'POST', body: JSON.stringify({ token }), credentials: 'include' }),
  staff2faDisable: (password) => request('/staff/2fa/disable', { method: 'POST', body: JSON.stringify({ password }), credentials: 'include' }),
  
  // User 2FA (non-staff)
  user2faStatus: (token) => request('/auth/2fa/status', { headers: { Authorization: `Bearer ${token}` } }),
  user2faSetup: (token) => request('/auth/2fa/setup', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  user2faEnable: (token, method, code) => request('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ method, token: code }), headers: { Authorization: `Bearer ${token}` } }),
  user2faDisable: (token, password) => request('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ password }), headers: { Authorization: `Bearer ${token}` } }),
  
  // Staff Audit Logs
  staffAuditLogs: (token, params) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.action) queryParams.append('action', params.action);
    return request(`/staff/audit-logs?${queryParams}`, { headers: { Authorization: `Bearer ${token}` } });
  },

  // Notifications
  notificationsGet: () => request('/notifications', { credentials: 'include' }),
  notificationGet: (id) => request(`/notifications/${id}`, { credentials: 'include' }),
  notificationMarkRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH', credentials: 'include' }),
  notificationMarkAllRead: () => request('/notifications/read-all', { method: 'POST', credentials: 'include' }),
  notificationDelete: (id) => request(`/notifications/${id}`, { method: 'DELETE', credentials: 'include' }),
  notificationCreate: (data) => request('/notifications', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  notificationSendToCompany: (data) => request('/notifications/send-to-company', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  notificationBroadcast: (data) => request('/notifications/broadcast', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  notificationAdminAll: () => request('/notifications/admin/all', { credentials: 'include' }),
  notificationsAdminArchived: (limit = 5, offset = 0) => request(`/notifications/admin/archived?limit=${limit}&offset=${offset}`, { credentials: 'include' }),
  notificationsArchived: (limit = 5, offset = 0) => request(`/notifications/archived?limit=${limit}&offset=${offset}`, { credentials: 'include' }),
  
  // Locations
  getLocations: () => request('/locations', { credentials: 'include' }),
  createLocation: (data) => request('/locations', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  updateLocation: (id, data) => request(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data), credentials: 'include' }),
  deleteLocation: (id) => request(`/locations/${id}`, { method: 'DELETE', credentials: 'include' }),
  getActionLocations: (actionId) => request(`/locations/action/${actionId}`, { credentials: 'include' }),
  setActionLocations: (actionId, locationIds) => request(`/locations/action/${actionId}`, { method: 'PUT', body: JSON.stringify({ locationIds }), credentials: 'include' }),
  
  // Public System Status (do not send cookies/credentials)
  systemStatus: () => request('/system/status', { credentials: 'omit' }),

  // Customer Auth & Account
  customerRegister: async (data) => {
    const resp = await request("/customer-auth/register", { method: "POST", body: JSON.stringify(data), skipRefresh: true });
    if (resp?.token) setAccessToken(resp.token);
    return resp;
  },
  customerLogin: async (data) => {
    const resp = await request("/customer-auth/login", { method: "POST", body: JSON.stringify(data), skipRefresh: true });
    if (resp?.token) setAccessToken(resp.token);
    return resp;
  },
  customerLogout: async () => {
    try { await request('/customer-auth/logout', { method: 'POST', skipRefresh: true }); } catch (_) {}
    clearAccessToken();
  },
  customerMe: () => request('/customer-auth/me', { credentials: 'include', skipRefresh: true }),
  customerUpdateProfile: (data) => request('/customer-auth/profile', { method: 'PUT', body: JSON.stringify(data), credentials: 'include', skipRefresh: true }),
  customerRequestPasswordReset: (email) => request('/customer-auth/request-password-reset', { method: 'POST', body: JSON.stringify({ email }), skipRefresh: true }),
  customerResetPassword: (token, newPassword) => request('/customer-auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword }), skipRefresh: true }),
  customerDeleteAccount: () => request('/customer-auth/account', { method: 'DELETE', credentials: 'include', skipRefresh: true }),
  customerVerifyEmail: (token) => request(`/customer-auth/verify-email?token=${encodeURIComponent(token)}`, { credentials: 'omit', skipRefresh: true }),
  customerResendVerification: (email) => request('/customer-auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }), credentials: 'omit', skipRefresh: true }),

  // Customer 2FA (TOTP)
  customer2faStatus: () => request('/customer-auth/2fa/status', { credentials: 'include', skipRefresh: true }),
  customer2faSetup: () => request('/customer-auth/2fa/setup', { method: 'POST', credentials: 'include', skipRefresh: true }),
  customer2faEnable: (token) => request('/customer-auth/2fa/enable', { method: 'POST', body: JSON.stringify({ token }), credentials: 'include', skipRefresh: true }),
  customer2faDisable: (password) => request('/customer-auth/2fa/disable', { method: 'POST', body: JSON.stringify({ password }), credentials: 'include', skipRefresh: true }),

  // Customer Stamp Cards
  customerStampCardsAvailable: () => request('/customer-stamp-cards/available', { credentials: 'include' }),
  customerStampCardsActive: () => request('/customer-stamp-cards/active', { credentials: 'include' }),
  customerStampCardActivate: (id) => request(`/customer-stamp-cards/${id}/activate`, { method: 'POST', credentials: 'include' }),
  customerStampCardDeactivate: (id) => request(`/customer-stamp-cards/${id}/deactivate`, { method: 'POST', credentials: 'include' }),

  // Customer Actions (QR-code requested actions)
  customerActions: () => request('/customer-actions', { credentials: 'include' }),
  customerActionActivate: (actionId) => request(`/customer-actions/${encodeURIComponent(actionId)}/activate`, { method: 'POST', credentials: 'include' }),

  // Organization Stamp Cards Management
  stampCardsGet: () => request('/stamp-cards', { credentials: 'include' }),
  stampCardGet: (id) => request(`/stamp-cards/${id}`, { credentials: 'include' }),
  stampCardCreate: (data) => request('/stamp-cards', { method: 'POST', body: JSON.stringify(data), credentials: 'include' }),
  stampCardUpdate: (id, data) => request(`/stamp-cards/${id}`, { method: 'PUT', body: JSON.stringify(data), credentials: 'include' }),
  stampCardDelete: (id) => request(`/stamp-cards/${id}`, { method: 'DELETE', credentials: 'include' }),
  stampCardUploadThumbnail: async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    const authHeader = currentAccessToken ? { Authorization: `Bearer ${currentAccessToken}` } : {};
    const res = await fetch(`${API_BASE}/stamp-cards/${encodeURIComponent(id)}/thumbnail`, {
      method: 'POST',
      headers: { ...authHeader },
      credentials: 'include',
      body: form,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = data?.error || `Upload failed ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data;
  },
  stampCardScan: (qrCode) => request(`/stamp-cards/scan/${encodeURIComponent(qrCode)}`, { method: 'POST', credentials: 'include' }),
  stampCardRedeem: (userCardId) => request(`/stamp-cards/redeem/${encodeURIComponent(userCardId)}`, { method: 'POST', credentials: 'include' }),

  // Agency Feature Pricing & Toggles
  agencyFeaturePricing: (token) => request('/agency/feature-pricing', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
  agencyToggleStampCards: (token, orgId, enable, confirmed = false) => 
    request(`/agency/organizations/${orgId}/toggle-stamp-cards`, { 
      method: 'POST', 
      body: JSON.stringify({ enable, confirmed }), 
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include' 
    }),
  agencyToggleNewsletter: (token, orgId, enable, confirmed = false) => 
    request(`/agency/organizations/${orgId}/toggle-newsletter`, { 
      method: 'POST', 
      body: JSON.stringify({ enable, confirmed }), 
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include' 
    }),
  agencyToggleShore: (token, orgId, enable, confirmed = false) =>
    request(`/agency/organizations/${orgId}/toggle-shore`, {
      method: 'POST',
      body: JSON.stringify({ enable, confirmed }),
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    }),

  // Shore Integration (ORG_OWNER)
  shoreGetConfig: (token) =>
    request('/shore/config', { headers: { Authorization: `Bearer ${token}` } }),
  shoreSaveConfig: (token, data) =>
    request('/shore/config', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${token}` },
    }),
  shoreTestEmail: (token, testEmail, actionId) =>
    request('/shore/config/test', {
      method: 'POST',
      body: JSON.stringify({ testEmail, ...(actionId ? { actionId } : {}) }),
      headers: { Authorization: `Bearer ${token}` },
    }),

  // System Dashboard - Customer Account Management
  systemCustomerAccounts: (page = 1, limit = 50, status = null, search = null) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return request(`/system/customer-accounts?${params}`, { credentials: 'include' });
  },
  systemCustomerAccount: (uuid) => request(`/system/customer-accounts/${uuid}`, { credentials: 'include' }),
  systemCustomerAccountBlock: (uuid) => request(`/system/customer-accounts/${uuid}/block`, { method: 'PUT', credentials: 'include' }),
  systemCustomerAccountUnblock: (uuid) => request(`/system/customer-accounts/${uuid}/unblock`, { method: 'PUT', credentials: 'include' }),
  systemCustomerAccountDelete: (uuid) => request(`/system/customer-accounts/${uuid}`, { method: 'DELETE', credentials: 'include' }),

  // System Dashboard - Feature Pricing Management
  systemFeaturePricing: () => request('/system/feature-pricing', { credentials: 'include' }),
  systemFeaturePricingUpdate: (featureName, data) => 
    request(`/system/feature-pricing/${featureName}`, { 
      method: 'PUT', 
      body: JSON.stringify(data), 
      credentials: 'include' 
    }),};

export default api;